import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Colors } from "utils/theme.utils";
import { FontFamily, Main, Models, OtpInput, PrimaryButton } from "utils/imports.utils";
import { toastifySuccess, useSetState } from "utils/functions.utils";
import { ButtonSize, ButtonTypes } from "utils/interface.utils";
import { STRINGS } from "constants/strings.constant";
import "./enter_otp.screen.scss";

// The mailed code is 6 digits (backend createotp / verifyOtp validation).
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const ERROR_VISIBLE_MS = 5000;
const VALIDATION_PREFIX = "Error validating request body.";

const readableError = (error: unknown, fallback: string): string => {
    const message = typeof error === "string" ? error : fallback;
    return message.startsWith(VALIDATION_PREFIX) ? message.slice(VALIDATION_PREFIX.length).trim() : message;
};

const formatCountdown = (seconds: number) => `0:${String(seconds).padStart(2, "0")}`;

const EnterOtpScreen = () =>  {
    const navigate = useNavigate();
    const location = useLocation();
    const email = (location.state as { email?: string } | null)?.email;

    const [state,setState] = useSetState({
        otp:"",
        error: "",
        activity: false,
        cooldown: RESEND_COOLDOWN_SECONDS,
    })

    useEffect(() => {
        if (!email) {
            navigate('/forgot-password');
        }
    }, [email, navigate]);

    useEffect(() => {
        if (state.cooldown <= 0) return;
        const timer = setTimeout(() => setState({ cooldown: state.cooldown - 1 }), 1000);
        return () => clearTimeout(timer);
    }, [state.cooldown]);

    // The message and the red outline clear themselves after a few seconds.
    useEffect(() => {
        if (!state.error) return;
        const timer = setTimeout(() => setState({ error: "" }), ERROR_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, [state.error]);

    const handleVerifyOtp = async () => {
        setState({ activity: true, error: "" });
        try {
            const response: any = await Models.auth.verifyOtp({ email, otp: state.otp });
            navigate('/reset-password', { state: { email, reset_password_hash: response.data.reset_password_hash } });
        } catch (error: any) {
            setState({ error: readableError(error, STRINGS.enterOtp.invalid) });
        } finally {
            setState({ activity: false });
        }
    };

    const handleResend = async () => {
        if (state.cooldown > 0) return;
        setState({ cooldown: RESEND_COOLDOWN_SECONDS, otp: "" });
        try {
            await Models.auth.sendOtp({ email });
            toastifySuccess(STRINGS.enterOtp.resent);
        } catch (error: any) {
            setState({ error: readableError(error, STRINGS.enterOtp.invalid) });
        }
    };

    return (
        <Main backgroundColor={Colors.screenBackground} isRenderOnlyLogo={true} logoSize={{width: "200px", height: "83px"}}>
            <div className="enter_otp">
                <div className="enter_otp_container">
                    <div className="enter_otp_icon" aria-hidden="true">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="5" width="18" height="14" rx="2" />
                            <path d="m3 7 9 6 9-6" />
                        </svg>
                    </div>
                    <div className="enter_otp_header">
                        <div className="enter_otp_header_title">{STRINGS.enterOtp.title}</div>
                        <div className="enter_otp_header_subtitle">
                            {STRINGS.enterOtp.sentTo}
                            <span className="enter_otp_header_email">{email}</span>
                        </div>
                    </div>
                    <div className={`enter_otp_component ${state.error ? "enter_otp_component_error" : ""}`}>
                        <OtpInput
                            numInputs={OTP_LENGTH}
                            isAutoFocus={true}
                            value={state.otp}
                            onChange={(value: string) => setState({otp:value, error: ""})}
                        />
                    </div>
                    {state.error && (
                        <div className="input_field_error enter_otp_error" role="alert">
                            <span className="enter_otp_error_icon" aria-hidden="true">!</span>
                            <span>{state.error}</span>
                        </div>
                    )}
                    <div className="enter_otp_buttons">
                        <div className="enter_otp_buttons_enter">
                            <PrimaryButton
                                onClick={handleVerifyOtp}
                                text={STRINGS.enterOtp.verify}
                                type={ButtonTypes.PRIMARY_GRADIENT}
                                size={ButtonSize.LARGE}
                                fontFamily={FontFamily.medium}
                                lineHeight="28px"
                                activity={state.activity}
                                disabled={state.activity || state.otp.length < OTP_LENGTH}
                            />
                        </div>
                    </div>
                    <div className="enter_otp_resend">
                        {STRINGS.enterOtp.didntGet}{" "}
                        {state.cooldown > 0 ? (
                            <span className="enter_otp_resend_wait">{STRINGS.enterOtp.resendIn} {formatCountdown(state.cooldown)}</span>
                        ) : (
                            <span className="enter_otp_resend_link" role="button" tabIndex={0} onClick={handleResend}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleResend(); }}>
                                {STRINGS.enterOtp.resend}
                            </span>
                        )}
                    </div>
                    <div className="enter_otp_buttons_back">
                        <PrimaryButton
                            onClick={() => navigate('/forgot-password')}
                            text={STRINGS.enterOtp.back}
                            type={ButtonTypes.TEXT_UNDERLINE}
                            size={ButtonSize.SMALL}
                            fontFamily={FontFamily.semiBold}
                        />
                    </div>
                </div>
            </div>
        </Main>
    )
}

export default EnterOtpScreen;
